import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  getUiArrangementRequest,
  saveUiArrangementRequest,
} from "../../../redux/settings/settingsSlice";
import { fetchCarouselRequest } from "../../../redux/slices/CarouselSlice";
import auth from "../../../configs/auth";
import { canShowPermission } from "../Permissions/permissionObject";

// helper for label
const formatLabel = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const UiArrangementPage = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.settingData);
  const carouselList = useSelector(
    (state) => state.carousel.data?.carousellist
  );

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const stored = localStorage.getItem("userPermissions");
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse userPermissions", e);
      }
    }
  }, []);

  const canEdit = canShowPermission(permissions, "uiArrangement", "edit");

  const [screens, setScreens] = useState([]);
  const [activeScreen, setActiveScreen] = useState(null);
  const [seriesMap, setSeriesMap] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [defaultSeriesMap, setDefaultSeriesMap] = useState({});
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const [selectedJewellerId, setSelectedJewellerId] = useState(() => {
    return sessionStorage.getItem(auth.selectedJeweller) || "";
  });
  const jewellerID = storedUser?.jeweller;

  const finalJewellerId =
    selectedJewellerId && selectedJewellerId.trim() !== ""
      ? selectedJewellerId
      : jewellerID;

  useEffect(() => {
    dispatch(getUiArrangementRequest());
    dispatch(fetchCarouselRequest());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      const map = {};
      const defaultMap = {};
      const customHomeWidgets = (carouselList || [])
        .filter(
          (carousel) =>
            carousel?.isSystem === false &&
            carousel?.location?.startsWith("home_")
        )
        .map((carousel) => carousel.location);

      const screenList = data.map((item) => {
        let sortedWidgets = (item.series || [])
          .slice()
          .sort((a, b) => a.priority - b.priority)
          .map((s) => s.widget);

        if (item.screen === "home_screen") {
          const existingWidgets = new Set(sortedWidgets);
          sortedWidgets = [
            ...sortedWidgets,
            ...customHomeWidgets.filter(
              (widget) => !existingWidgets.has(widget)
            ),
          ];
        }

        map[item.screen] = sortedWidgets;
        defaultMap[item.screen] = sortedWidgets;

        return {
          key: item.screen,
          label: formatLabel(item.screen),
        };
      });

      setSeriesMap(map);
      setDefaultSeriesMap(defaultMap);
      setScreens(screenList);

      if (!activeScreen) {
        setActiveScreen(screenList[0]);
      }
    }
  }, [data, carouselList]);

  const carouselLabels = Object.fromEntries(
    (carouselList || []).map((carousel) => [
      carousel.location,
      carousel.label || formatLabel(carousel.location),
    ])
  );

  if (!activeScreen) return null;

  const series = seriesMap[activeScreen.key] || [];
  const isDefaultOrder =
    JSON.stringify(series) ===
    JSON.stringify(defaultSeriesMap[activeScreen.key] || []);

  // -------- Drag Logic --------
  const handleDragStart = (item) => setDraggedItem(item);

  const handleDragOver = (e, item) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === item) return;

    const items = [...series];
    const fromIndex = items.indexOf(draggedItem);
    const toIndex = items.indexOf(item);

    items.splice(fromIndex, 1);
    items.splice(toIndex, 0, draggedItem);

    setSeriesMap({
      ...seriesMap,
      [activeScreen.key]: items,
    });
  };

  const handleDragEnd = () => setDraggedItem(null);

  // -------- Save Logic --------
  const handleSave = () => {
    if (!series.length) return;

    const payloadSeries = series.map((widget, index) => ({
      widget,
      priority: index + 1,
    }));

    const payload = {
      jeweller: finalJewellerId,
      screen: activeScreen.key,
      series: payloadSeries,
    };

    dispatch(saveUiArrangementRequest(payload));
  };

  const handleSetDefaultArrangement = () => {
    const defaultSeries = defaultSeriesMap[activeScreen.key] || [];
    if (!defaultSeries.length) return;

    setSeriesMap((prev) => ({
      ...prev,
      [activeScreen.key]: [...defaultSeries],
    }));
  };

  return (
    <Box sx={{ display: "flex", background: "#f4f6f8", mt: 0, minHeight: "100vh" }}>
      {/* Sidebar */}
      <Paper sx={{ width: 260, p: 2, borderRadius: 0, m: 2 }}>
        <Typography variant="h6" gutterBottom>
          Screens
        </Typography>
        <List>
          {screens.map((screen) => (
            <ListItem
              key={screen.key}
              button
              selected={activeScreen.key === screen.key}
              onClick={() => setActiveScreen(screen)}
              sx={{
                borderRadius: 1,
                mb: 1,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#e3f2fd",
                },
                "&.Mui-selected": {
                  backgroundColor: "#1976d2",
                  color: "#000",
                  fontWeight: 600,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "#1565c0",
                },
              }}
            >
              <ListItemText
                primary={screen.label}
                sx={{
                  "& .MuiListItemText-primary": {
                    color: activeScreen.key === screen.key ? "#000" : "inherit",
                    fontWeight: activeScreen.key === screen.key ? 600 : 400,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, p: 3, m: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">
            {activeScreen.label} – UI Arrangement
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSetDefaultArrangement}
              disabled={!canEdit || !series.length || isDefaultOrder}
            >
              Set Default Arrangement
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                color="success"
                onClick={handleSave}
                disabled={!series.length}
              >
                Save Arrangement
              </Button>
            )}
          </Box>
        </Box>

        {
          <Box
            sx={{
              mb: 2,
              px: 2,
              py: 1,
              backgroundColor: "#e8f5e9",
              borderRadius: 1,
              border: "1px solid #81c784",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#2e7d32", fontWeight: 600 }}
            >
              Recommended & Best (Default Arrangement)
            </Typography>
          </Box>
        }

        {/* Draggable List */}
        <Paper>
          <List>
            {series.map((item, index) => (
              <React.Fragment key={item}>
                <ListItem
                  draggable={canEdit}
                  onDragStart={canEdit ? () => handleDragStart(item) : undefined}
                  onDragOver={canEdit ? (e) => handleDragOver(e, item) : undefined}
                  onDragEnd={canEdit ? handleDragEnd : undefined}
                  sx={{
                    cursor: canEdit ? (draggedItem === item ? "grabbing" : "grab") : "default",
                    userSelect: "none",
                    backgroundColor:
                      draggedItem === item ? "#eeeeee" : "transparent",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  {canEdit && <DragIndicatorIcon sx={{ mr: 1, color: "#9e9e9e" }} />}
                  <ListItemText
                    primary={`${index + 1}. ${
                      carouselLabels[item] || formatLabel(item)
                    }`}
                  />
                </ListItem>
                {index !== series.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>
    </Box>
  );
};

export default UiArrangementPage;
